// submenu (dropdown) behaviour — backs the searchable-select options menu, the
// selectMultiple tags dropdown and the actionsGroup overflow menus.
//
// cleanSlate has no framework dropdown (bootstrap5 uses bootstrap.Dropdown,
// metronic_8 KTMenu), so each per-menu instance is managed here directly.
// selectSearchable.js calls createInstances() then get(optionsMenu), then
// .on('shown'|'hidden') + .show(inputHolder); scripts/menu.js opens an
// actionsGroup panel with .show(trigger) and closes it with .hide().

// Gap (px) between the select control and its dropdown menu.
const SUBMENU_GAP = 4;

// Build (once) the submenu instance for a menu element (the optionsMenu div,
// data-zit-menu="true"): open/close, fixed positioning so the panel escapes the
// window card's overflow:hidden clip, outside-click + page-scroll close, and the
// neutral shown/hidden lifecycle events. The component wires its own trigger
// (displayInput focus + optionsMenuTrigger click both call .show(inputHolder));
// this adapter never wires the trigger itself.
function ensureSubmenuInstance(menuEl) {
    if (menuEl.zitSubmenuInstance) {
        return menuEl.zitSubmenuInstance;
    }

    const handlers = { shown: [], hidden: [] };
    let isOpen = false;
    let anchorEl = null;

    const fire = function (name, el) {
        handlers[name].forEach((handler) => handler(el));
    };

    // Pin the panel under the control using the control's viewport rect. Fixed
    // positioning detaches it from any ancestor overflow:hidden (the card /
    // collapsible region) that would otherwise clip the dropdown.
    //
    // Horizontal alignment follows the anchor's data-zit-menu-placement: a value
    // ending in "end" (actionsGroup's default "bottom-end") right-aligns the panel
    // with the anchor, anything else (selectSearchable's "bottom-start", or none)
    // left-aligns it. Called after the shown class is applied, so the panel has a
    // measurable width.
    const position = function () {
        if (anchorEl === null) {
            return;
        }
        const rect = anchorEl.getBoundingClientRect();
        menuEl.style.position = 'fixed';
        menuEl.style.top = (rect.bottom + SUBMENU_GAP) + 'px';

        const placement = anchorEl.getAttribute('data-zit-menu-placement') || '';

        if (placement.endsWith('end')) {
            // right edge on the anchor's right edge, but never off the viewport's left
            const left = rect.right - menuEl.offsetWidth;
            menuEl.style.left = Math.max(SUBMENU_GAP, left) + 'px';
        } else {
            menuEl.style.left = rect.left + 'px';
            // Never narrower than the control (selectSearchable also seeds minWidth).
            // Not applied to an end-aligned menu: its anchor is a small icon trigger.
            if (parseFloat(menuEl.style.minWidth || '0') < rect.width) {
                menuEl.style.minWidth = rect.width + 'px';
            }
        }
    };

    // Close when a pointerdown lands outside both the panel and its anchor
    // (clicking the trigger is on the anchor, so reopen doesn't immediately close).
    const onDocPointer = function (e) {
        if (!menuEl.contains(e.target) && (anchorEl === null || !anchorEl.contains(e.target))) {
            instance.hide();
        }
    };

    // A page scroll/resize would leave the fixed panel detached from the moving
    // control, so close it — but ignore scrolls inside the panel's own options
    // list (that scroll is expected and must not dismiss the menu).
    const onReposition = function (e) {
        if (e && e.target && e.target.nodeType === 1 && menuEl.contains(e.target)) {
            return;
        }
        instance.hide();
    };

    const instance = {
        on: function (name, handler) {
            if (typeof handlers[name] === typeof undefined) {
                throw new Error('zit submenu.on: unknown event "' + name + '"');
            }
            handlers[name].push(handler);
        },
        show: function (targetEl) {
            anchorEl = targetEl || menuEl.parentElement;
            if (isOpen) {
                position();
                return;
            }
            isOpen = true;
            menuEl.classList.add('zit-cs-menu-shown');
            position();
            // Defer the document listeners so the opening click doesn't close it.
            setTimeout(() => {
                document.addEventListener('pointerdown', onDocPointer, true);
                window.addEventListener('scroll', onReposition, true);
                window.addEventListener('resize', onReposition, true);
            }, 0);
            fire('shown', menuEl);
        },
        hide: function () {
            if (!isOpen) {
                return;
            }
            isOpen = false;
            menuEl.classList.remove('zit-cs-menu-shown');
            document.removeEventListener('pointerdown', onDocPointer, true);
            window.removeEventListener('scroll', onReposition, true);
            window.removeEventListener('resize', onReposition, true);
            fire('hidden', menuEl);
        }
    };

    menuEl.zitSubmenuInstance = instance;
    return instance;
}

const submenu = {
    // Pre-build instances for every menu currently in the DOM (idempotent —
    // ensureSubmenuInstance caches on the element). Called by the component on
    // init in case the menu was inserted after the boot-time pass.
    createInstances: function () {
        const menus = document.querySelectorAll('[data-zit-menu="true"]');
        menus.forEach((menuEl) => ensureSubmenuInstance(menuEl));
    },
    // Resolve the instance for a menu element (selectSearchable passes the
    // optionsMenu, which carries data-zit-menu); also accept an ancestor.
    get: function (el) {
        const menuEl = el.matches('[data-zit-menu="true"]')
            ? el
            : el.querySelector('[data-zit-menu="true"]');

        if (menuEl === null) {
            return null;
        }

        return ensureSubmenuInstance(menuEl);
    }
};

export default submenu;
